import { ParsedUrlQueryInput, stringify } from 'querystring';
import md5 from 'md5';
import { QueryValue } from '@/services/api-types';
import { TokenCreateResult } from '@/services/personal-center/types';
import { createClientLogger } from '@/lib/logger/client-logger';
import { isApiLogEnabled } from '@/lib/logger/config';

const logger = createClientLogger('api-utils');

/**
 * 类型安全的查询参数构建函数
 * 基于 Node.js 内置的 querystring.stringify 实现
 */
export const buildQueryString = (
    params: Record<string, QueryValue>,
    fullUrl: boolean = true,
): string => {
    // 过滤掉 null 和 undefined
    const filteredParams: ParsedUrlQueryInput = Object.entries(params).reduce(
        (acc, [key, value]) => {
            if (value === null || value === undefined) {
                return acc;
            }

            // 处理数组类型
            if (Array.isArray(value)) {
                acc[key] = value.filter(
                    item => item !== null && item !== undefined,
                );
                return acc;
            }

            // 处理布尔值（转换为字符串，因为 URL 中没有布尔类型）
            if (typeof value === 'boolean') {
                acc[key] = value.toString();
                return acc;
            }

            // 其他基本类型直接转换为字符串
            acc[key] = String(value);
            return acc;
        },
        {} as ParsedUrlQueryInput,
    );

    // 使用 Node.js 内置的 stringify 方法（Next.js 环境支持）
    const queryStr = stringify(filteredParams);
    return (
        (fullUrl ? process.env.NEXT_PUBLIC_API_BASE : '') +
        (queryStr ? `?${queryStr}` : '')
    );
};

/**
 * 解析查询字符串为对象（使用 URLSearchParams）
 */
export const parseQueryString = (
    queryString: string,
): Record<string, string | string[]> => {
    const params = new URLSearchParams(queryString);
    const result: Record<string, string | string[]> = {};

    for (const [key, value] of params.entries()) {
        if (result[key]) {
            // 如果键已存在，转换为数组
            if (Array.isArray(result[key])) {
                (result[key] as string[]).push(value);
            } else {
                result[key] = [result[key] as string, value];
            }
        } else {
            result[key] = value;
        }
    }

    return result;
};

/**
 * 获取认证头部信息
 * 包含 Content-Type, Authorization, API-Seckey, API-Timestamp
 * 注意：Api-Origin 由服务端 api-proxy.ts 统一设置，客户端不再发送
 * 如果没有 token 则返回 null
 */
export const getAuthHeaders = ():
    | {
          'Content-Type': string;
          'Authorization': string;
          'Api-Seckey': string;
          'Api-Timestamp': string;
      }
    | { 'Content-Type': string }
    | null => {
    if (typeof window === 'undefined') return null; // 确保在浏览器环境中执行
    const token = localStorage.getItem('token');
    const origin = process.env.NEXT_PUBLIC_ORIGIN || '';
    if (token) {
        const tokenObj = JSON.parse(token) as TokenCreateResult;
        const timestamp = Math.floor(new Date().getTime() / 1000).toString();
        const signature = `${tokenObj.accessToken}${tokenObj.salt}${timestamp}${tokenObj.accessToken}${origin}`;
        if (isApiLogEnabled()) {
            logger.info('认证头部信息', {
                tokenObj,
                timestamp,
                signature,
            });
        }
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenObj.accessToken}`,
            'Api-Seckey': md5(signature),
            'Api-Timestamp': timestamp,
            // Api-Origin 由服务端统一设置，客户端不再发送
        };
    }
    return {
        'Content-Type': 'application/json',
    };
};
